import { gql, useApolloClient } from "@apollo/client";
import { Intent } from "@blueprintjs/core";
import {
  CellType,
  ClientAnalyticsEventType,
  ColumnSemanticLayerType,
  DISPLAY_TABLE_DEFAULTS,
  DataBrowserTab,
  DataBrowserTableSelectedEventData,
  DataConnectionId,
  DataConnectionType,
  DataSourceTableId,
  NoCodeCellType,
  SemanticProjectId,
  SpecialVersionType,
  TableType,
  buildLimitedQuery,
  createChartCellPayload,
  createDisplayTableCellPayload,
  createExploreCellPayload,
  createFilterCellPayload,
  createPivotCellPayload,
  createSqlCellPayload,
  generateDataframeNameForDataSourceTable,
  generateTableNameSQL,
  getTablePath,
  guardNever,
  hexIdToHumanHexId,
  newSuggestibleChart,
} from "@hex/common";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  SemanticDataset,
  SemanticView,
} from "../../../generated/graphqlTypes.js";
import {
  DEFAULT_DISPLAY_TABLE_RESULT_VARIABLE,
  DEFAULT_FILTER_RESULT_VARIABLE,
  DEFAULT_PIVOT_RESULT_VARIABLE,
  DEFAULT_SQL_DATAFRAME_NAME,
} from "../../../hooks/cell/useGetNewCellFields.js";
import { Routes } from "../../../route/routes.js";
import { trackEvent } from "../../../util/trackEvent.js";
import { useToaster } from "../../common/Toasts.js";
import { DEFAULT_PROJECT_TITLE } from "../../logic/shared.js";

import {
  GetTableColumnsForMetricViewDocument,
  GetTableColumnsForMetricViewQuery,
  GetTableColumnsForMetricViewQueryVariables,
  useCreateHexFromHomeMutation,
} from "./useHomeTableActions.generated.js";

gql`
  mutation CreateHexFromHome(
    $name: String!
    $description: String
    $dataConnectionIds: [DataConnectionId!]
    $cellsToCreate: [CellContentsPayload!]
  ) {
    createHex(
      name: $name
      description: $description
      dataConnectionIds: $dataConnectionIds
      cellsToCreate: $cellsToCreate
    ) {
      id
    }
  }
`;

gql`
  query GetTableColumnsForMetricView($dataSourceTableId: DataSourceTableId!) {
    dataSourceTableById(dataSourceTableId: $dataSourceTableId) {
      id
      columns {
        id
        name
        columnSemanticLayerType
      }
    }
  }
`;

interface UseHomeTableActionsResult {
  createChartCell: () => void;
  createSqlCell: () => void;
  createPivotCell: () => void;
  createFilterCell: () => void;
  createDisplayTableCell: () => void;
  createExploreCell: () => void;
}

export function useHomeTableActions({
  browserType,
  connectionType,
  dataConnectionId,
  databaseName,
  multiDatabase,
  schemaName,
  tab,
  tableId,
  tableName,
  tableType,
}: {
  tableName: string | null;
  schemaName: string | null;
  databaseName: string | null;
  tableType: TableType | null;
  dataConnectionId: DataConnectionId;
  multiDatabase: boolean;
  tab: DataBrowserTab;
  tableId: DataSourceTableId | null;
  connectionType: DataConnectionType;
  browserType: DataBrowserTableSelectedEventData["browserType"];
}): UseHomeTableActionsResult {
  const [createHex] = useCreateHexFromHomeMutation();

  const navigate = useNavigate();
  const toaster = useToaster();
  const client = useApolloClient();

  const getTablePathCallback = useCallback(() => {
    if (tableName == null || schemaName == null) {
      return;
    }
    const tablePath = getTablePath({
      databaseName,
      includeDatabaseName: multiDatabase,
      schemaName,
      tableName,
    });

    return generateTableNameSQL({
      tablePath,
      dataConnectionType: connectionType,
    });
  }, [connectionType, databaseName, multiDatabase, schemaName, tableName]);

  const getNoCodeDataframe = useCallback(() => {
    const path = getTablePathCallback();
    if (tableId == null || path == null) {
      return;
    }
    const config = {
      pathToTable: path,
      dataSourceTableId: tableId,
      connectionId: dataConnectionId,
    };

    return {
      ...config,
      dataframeName: generateDataframeNameForDataSourceTable(config),
    };
  }, [dataConnectionId, getTablePathCallback, tableId]);

  const trackTableSelected = useCallback(
    (cellType: NoCodeCellType | typeof CellType.SQL) => {
      trackEvent(ClientAnalyticsEventType.DATA_BROWSER_TABLE_SELECTED, {
        browserType,
        tableActionTaken: cellType,
        dataSourceTableId: tableId ?? null,
        dataConnectionId,
        dataBrowserTab: tab,
      });
    },
    [browserType, tableId, dataConnectionId, tab],
  );
  const createHexWithCellContents = useCallback(
    async (cellType: NoCodeCellType | typeof CellType.SQL) => {
      let cellContents;
      switch (cellType) {
        case CellType.CHART:
          cellContents = createChartCellPayload({
            chartSpec: newSuggestibleChart(getNoCodeDataframe()),
          });
          break;
        case CellType.DISPLAY_TABLE:
          cellContents = createDisplayTableCellPayload({
            pageSize: DISPLAY_TABLE_DEFAULTS.pageSize,
            resultVariable: DEFAULT_DISPLAY_TABLE_RESULT_VARIABLE,
            dataframe: getNoCodeDataframe(),
          });
          break;
        case CellType.PIVOT:
          cellContents = createPivotCellPayload({
            resultVariable: DEFAULT_PIVOT_RESULT_VARIABLE,
            castDecimals: true,
            dataframe: getNoCodeDataframe(),
            createAsExplore: true,
          });
          break;
        case CellType.FILTER:
          cellContents = createFilterCellPayload({
            resultVariable: DEFAULT_FILTER_RESULT_VARIABLE,
            castDecimals: true,
            dataframe: getNoCodeDataframe(),
          });
          break;
        case CellType.SQL:
          let columns: Array<{ name: string }> | undefined = undefined;
          if (tableType === TableType.METRIC_VIEW && tableId != null) {
            const fetchedData = (
              await client.query<
                GetTableColumnsForMetricViewQuery,
                GetTableColumnsForMetricViewQueryVariables
              >({
                query: GetTableColumnsForMetricViewDocument,
                variables: { dataSourceTableId: tableId },
              })
            ).data;
            columns = fetchedData.dataSourceTableById.columns
              .filter(
                (c) =>
                  c.columnSemanticLayerType !== ColumnSemanticLayerType.MEASURE,
              )
              .map((c) => ({ name: c.name }));
          }
          cellContents = createSqlCellPayload({
            resultVariable: DEFAULT_SQL_DATAFRAME_NAME,
            castDecimals: true,
            connectionId: dataConnectionId,
            source: buildLimitedQuery({
              connectionType,
              columns,
              tablePath: getTablePathCallback() ?? "",
            }),
          });
          break;
        case CellType.EXPLORE:
          cellContents = createExploreCellPayload({
            input: getNoCodeDataframe(),
          });
          break;
        default:
          guardNever(cellType, cellType);
      }
      trackTableSelected(cellType);
      if (cellContents) {
        createHex({
          variables: {
            name: DEFAULT_PROJECT_TITLE,
            description: "",
            dataConnectionIds: [dataConnectionId],
            cellsToCreate: [cellContents],
          },
        })
          .then((result) => {
            if (result.data?.createHex.id) {
              navigate(
                Routes.LOGIC.getUrl({
                  hexLocator: hexIdToHumanHexId(
                    result.data?.createHex.id,
                    DEFAULT_PROJECT_TITLE,
                  ),
                  version: SpecialVersionType.DRAFT,
                }),
              );
            } else {
              toaster.show({
                message: "Failed to create project",
                intent: Intent.DANGER,
              });
            }
          })
          .catch((e) => {
            console.error(e);
            toaster.show({
              message: "Failed to create project",
              intent: Intent.DANGER,
            });
          });
      }
    },
    [
      trackTableSelected,
      getNoCodeDataframe,
      tableType,
      tableId,
      dataConnectionId,
      connectionType,
      getTablePathCallback,
      client,
      createHex,
      navigate,
      toaster,
    ],
  );

  const createChartCell = useCallback(() => {
    void createHexWithCellContents(CellType.CHART);
  }, [createHexWithCellContents]);

  const createDisplayTableCell = useCallback(() => {
    void createHexWithCellContents(CellType.DISPLAY_TABLE);
  }, [createHexWithCellContents]);

  const createFilterCell = useCallback(() => {
    void createHexWithCellContents(CellType.FILTER);
  }, [createHexWithCellContents]);

  const createPivotCell = useCallback(() => {
    void createHexWithCellContents(CellType.PIVOT);
  }, [createHexWithCellContents]);

  const createSqlCell = useCallback(() => {
    void createHexWithCellContents(CellType.SQL);
  }, [createHexWithCellContents]);

  const createExploreCell = useCallback(() => {
    void createHexWithCellContents(CellType.EXPLORE);
  }, [createHexWithCellContents]);

  return {
    createChartCell,
    createSqlCell,
    createPivotCell,
    createFilterCell,
    createDisplayTableCell,
    createExploreCell,
  };
}

interface UseHomeTableSemanticDatasetActionsResult {
  createExploreCell: () => void;
}

export function useHomeTableSemanticResourceActions({
  browserType,
  dataConnectionId,
  semanticProjectId,
  semanticResource,
  tab,
}: {
  dataConnectionId: DataConnectionId;
  tab: DataBrowserTab;
  semanticResource:
    | Pick<SemanticDataset, "__typename" | "name" | "id">
    | Pick<SemanticView, "__typename" | "name" | "id">;
  semanticProjectId: SemanticProjectId;
  browserType: DataBrowserTableSelectedEventData["browserType"];
}): UseHomeTableSemanticDatasetActionsResult {
  const [createHex] = useCreateHexFromHomeMutation();

  const navigate = useNavigate();
  const toaster = useToaster();

  const createHexWithCellContents = useCallback(
    (cellType: typeof CellType.EXPLORE) => {
      let cellContents;
      switch (cellType) {
        case CellType.EXPLORE:
          if (semanticResource.__typename === "SemanticDataset") {
            cellContents = createExploreCellPayload({
              input: {
                semanticDatasetName: semanticResource.name,
                semanticProjectId,
                dataConnectionId,
              },
            });
          } else if (semanticResource.__typename === "SemanticView") {
            cellContents = createExploreCellPayload({
              input: {
                semanticViewName: semanticResource.name,
                semanticProjectId,
                dataConnectionId,
              },
            });
          } else {
            guardNever(semanticResource, semanticResource);
          }
          break;
        default:
          guardNever(cellType, cellType);
      }

      if (semanticResource.__typename === "SemanticDataset") {
        trackEvent(ClientAnalyticsEventType.SEMANTIC_DATASET_SELECTED, {
          browserType,
          tableActionTaken: "EXPLORE",
          semanticDatasetId: semanticResource.id,
          semanticProjectId,
          dataBrowserTab: tab,
        });
      } else if (semanticResource.__typename === "SemanticView") {
        trackEvent(ClientAnalyticsEventType.SEMANTIC_VIEW_SELECTED, {
          browserType,
          tableActionTaken: "EXPLORE",
          semanticViewId: semanticResource.id,
          semanticProjectId,
          dataBrowserTab: tab,
        });
      } else {
        guardNever(semanticResource, semanticResource);
      }

      if (cellContents) {
        createHex({
          variables: {
            name: DEFAULT_PROJECT_TITLE,
            description: "",
            dataConnectionIds: [dataConnectionId],
            cellsToCreate: [cellContents],
          },
        })
          .then((result) => {
            if (result.data?.createHex.id) {
              navigate(
                Routes.LOGIC.getUrl({
                  hexLocator: hexIdToHumanHexId(
                    result.data?.createHex.id,
                    DEFAULT_PROJECT_TITLE,
                  ),
                  version: SpecialVersionType.DRAFT,
                }),
              );
            } else {
              toaster.show({
                message: "Failed to create project",
                intent: Intent.DANGER,
              });
            }
          })
          .catch((e) => {
            console.error(e);
            toaster.show({
              message: "Failed to create project",
              intent: Intent.DANGER,
            });
          });
      }
    },
    [
      semanticResource,
      semanticProjectId,
      dataConnectionId,
      browserType,
      tab,
      createHex,
      navigate,
      toaster,
    ],
  );

  const createExploreCell = useCallback(() => {
    createHexWithCellContents(CellType.EXPLORE);
  }, [createHexWithCellContents]);

  return {
    createExploreCell,
  };
}
